import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Chip,
  Grid,
  Divider,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalePage() {
  const pageTitle = "Sales Order";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [resultData, setResultData] = useState({});
  const [sale, setSale] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/ops/sales/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data);
            const saleData = result.data.data?.sale || result.data.data || {};
            setSale(saleData);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data?.message || "Failed to fetch sales order details");
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  function singularize(word) {
    if (!word || typeof word !== "string") return word;

    const irregular = {
      people: "person",
      men: "man",
      women: "woman",
      children: "child",
      teeth: "tooth",
      feet: "foot",
      mice: "mouse",
      geese: "goose",
      data: "datum",
      indices: "index",
      analyses: "analysis",
      crises: "crisis",
      diagnoses: "diagnosis",
      theses: "thesis",
      lives: "life",
      wives: "wife",
      knives: "knife",
      leaves: "leaf",
      shelves: "shelf",
      wolves: "wolf",
    };

    const lower = word.toLowerCase();

    if (irregular[lower]) {
      return matchCase(word, irregular[lower]);
    }

    if (lower.endsWith("ies") && lower.length > 3) {
      return matchCase(word, lower.slice(0, -3) + "y");
    }

    if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      return matchCase(word, lower.slice(0, -2));
    }

    if (lower.endsWith("ves")) {
      return matchCase(word, lower.slice(0, -3) + "f");
    }

    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return matchCase(word, lower.slice(0, -1));
    }

    return word;
  }

  function matchCase(original, replacement) {
    return original[0] === original[0].toUpperCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  }

  // Helpers
  const formatKoboToNaira = (amountInKobo) => {
    const nairaAmount = (Number(amountInKobo) || 0) / 100;
    return `${resultData?.currency}${nairaAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusChip = (status, paid, successful) => {
    if (successful || status === "SUCCESS") {
      return (
        <Chip
          label="SUCCESS"
          color="success"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold" }}
        />
      );
    }
    if (status === "AWAITING-PAYMENT") {
      return (
        <Chip
          label={String(status).toUpperCase()}
          color="warning"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold" }}
        />
      );
    }
    if (status === "FAILED" || status === "ERROR") {
      return (
        <Chip
          label={String(status).toUpperCase()}
          color="error"
          size="small"
          sx={{ color: "#fff", fontWeight: "bold" }}
        />
      );
    }
    return (
      <Chip
        label={String(status || "").toUpperCase()}
        color="default"
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    );
  };

  // DataTable structure for Desktop Line Items
  const tableColumns = [
    { Header: "Product", accessor: "product", width: "35%" },
    { Header: "SKU", accessor: "sku", width: "15%", align: "center" },
    { Header: "Qty", accessor: "quantity", align: "center" },
    { Header: "Unit Price", accessor: "unit_price", align: "right" },
    { Header: "Total", accessor: "total", align: "right" },
  ];

  const tableRows = (sale.items || []).map((item, index) => ({
    product: (
      <MDTypography variant="button" fontWeight="medium" sx={{ wordBreak: "break-all" }}>
        {item.product.title}
      </MDTypography>
    ),
    sku: item.product.sku ? item.product.sku : "---",
    quantity: (
      <MDTypography variant="button" fontWeight="regular">
        {item.quantity}
      </MDTypography>
    ),
    unit_price: (
      <MDTypography variant="button" fontWeight="regular">
        {formatKoboToNaira(item.unit_price)}
      </MDTypography>
    ),
    total: (
      <MDTypography variant="button" fontWeight="bold" color="dark">
        {formatKoboToNaira(item.total)}
      </MDTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} pb={3} mb={5}>
        <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            color="dark"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </Button>

          {/* {sale?.reference && (
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => navigate(`/agents/ops/sales/payment/${sale.reference}`)}
              startIcon={<Icon>payments</Icon>}
            >
              Payment Details
            </MDButton>
          )} */}
        </MDBox>

        {pageLoaded && (
          <MDBox mt={1}>
            {/* Summary Details Header Card */}
            <Card sx={{ p: { xs: 2.5, sm: 2 }, mb: 3 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={1}
              >
                <MDBox mt={-1}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    textTransform="uppercase"
                    fontWeight="bold"
                  >
                    Reference
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ wordBreak: "break-all" }}>
                    {sale.reference}
                  </MDTypography>
                </MDBox>
                <MDBox>{getStatusChip(sale.status, sale.paid, sale.successful)}</MDBox>
              </MDBox>

              <Divider sx={{ mt: 2, mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">
                    Sales Rule
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="bold" color="dark">
                    {sale.rule}
                  </MDTypography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">
                    {singularize(sale.entity.type)}
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="bold" color="dark">
                    {sale.entity?.title}
                  </MDTypography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">
                    Agent
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {sale.agent || "N/A"}
                  </MDTypography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">
                    Date Created
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {sale.created}
                  </MDTypography>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <MDTypography variant="caption" color="text" display="block">
                    Total Order Value
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold" color="success">
                    {formatKoboToNaira(sale.amount)}
                  </MDTypography>
                </Grid>
              </Grid>
            </Card>

            {/* Line Items Card */}
            <Card sx={{ height: "100%" }}>
              <MDBox mb={3}>
                <MDBox p={2.5} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" fontWeight="medium">
                    Order Line Items
                  </MDTypography>
                  <Chip
                    label={`Items: ${sale.items?.length || 0}`}
                    size="small"
                    variant="outlined"
                  />
                </MDBox>

                {sale.items && sale.items.length > 0 ? (
                  <DataTable
                    table={{ columns: tableColumns, rows: tableRows }}
                    isSorted={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                      No items in this sales order.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
